// Shared width rule for the portaled option lists of SearchableDropdown and MultiSelectDropdown
// (W-M19).
//
// The menus used to be pinned to exactly the trigger's width. In a 200px filter field that
// wrapped a long option onto two or three lines, with the checkbox floating beside the middle of
// the block. The rule now is:
//   - at least as wide as the field it opens from,
//   - growing to fit the longest option (CSS `max-content`) up to MENU_MAX_WIDTH,
//   - never past the right edge of the viewport,
//   - and beyond that, each option ellipsizes on a single line with its full text as a title.

/** Widest a dropdown menu grows to fit its longest option before options ellipsize. */
export const MENU_MAX_WIDTH = 400;
const VIEWPORT_PAD = 8;

export interface MenuWidthBounds {
  /** Left edge (viewport px). Shifted left only when even the field width would overflow. */
  left: number;
  minWidth: number;
  maxWidth: number;
}

export function menuWidthBounds(
  triggerLeft: number,
  triggerWidth: number,
  viewportWidth: number,
  minWidthFloor = 0,
): MenuWidthBounds {
  const usable = Math.max(0, viewportWidth - VIEWPORT_PAD * 2);
  const wanted = Math.max(triggerWidth, minWidthFloor);
  const minWidth = usable ? Math.min(wanted, usable) : wanted;
  let left = triggerLeft;
  if (usable && left + minWidth > viewportWidth - VIEWPORT_PAD) {
    left = Math.max(VIEWPORT_PAD, viewportWidth - VIEWPORT_PAD - minWidth);
  }
  if (usable && left < VIEWPORT_PAD) left = VIEWPORT_PAD;
  const room = usable ? viewportWidth - VIEWPORT_PAD - left : Number.POSITIVE_INFINITY;
  const maxWidth = Math.max(minWidth, Math.min(Math.max(minWidth, MENU_MAX_WIDTH), room));
  return { left, minWidth, maxWidth };
}

/**
 * Class list for one option row: left-aligned (a <button> centres its text by default, which is
 * what centred every option app-wide) and top-aligned so a leading checkbox sits on the first
 * text line.
 */
export const DROPDOWN_OPTION_CLASS =
  'wrap-dropdown-option tw-flex tw-w-full tw-items-start tw-gap-2 tw-text-left tw-text-sm';

/** The option's text: one line, ellipsized at the menu edge. Pair with a `title`. */
export const DROPDOWN_OPTION_LABEL_CLASS = 'tw-min-w-0 tw-flex-1 tw-truncate';
